/**
 * @file LeadCentralModal.jsx
 * @description Central Omnichannel do lead — BLB System (Comercial / Pipeline).
 *
 * Substitui o antigo LeadDrawer lateral por um MODAL CENTRAL, de duas colunas:
 *   - Coluna esquerda: dossiê do lead (dados, sinais, ações rápidas).
 *   - Coluna direita: timeline unificada (WhatsApp/SMS/E-mail/Nota/Sistema em um
 *     único fio cronológico) + composer estilo chat, com abas de canal.
 *
 * Reaproveita os endpoints JÁ existentes em routesSales.js (/api/vendas/*):
 *   GET  /lead/:id/timeline
 *   POST /lead/:id/nota   | /sms | /email | /whatsapp   (envio inline)
 *
 * Mantém a MESMA interface de props do LeadDrawer (lead, isOpen, onClose,
 * onAfterAction), então a troca em SystemHubModalVendas.js é 1 linha.
 *
 * Blindagem de conversão: o botão "Converter em Tenant" só aparece nos estágios
 * liberados E some assim que a timeline já registra uma conversão iniciada —
 * matando o clique-múltiplo que gerava envelopes ClickSign duplicados.
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import { toast } from 'react-toastify';
import ModalConverterTenant from './ModalConverterTenant';
import './LeadCentralModal.css';
import {
  FaTimes, FaHistory, FaRegStickyNote, FaSms, FaEnvelope, FaWhatsapp,
  FaUserTie, FaDollarSign, FaInfoCircle, FaPaperPlane, FaFileSignature,
  FaFire, FaSnowflake, FaSmog, FaBolt, FaExclamationTriangle, FaPhone,
  FaAt, FaMapSigns, FaMagic, FaCheckDouble, FaClock,
  FaPaperclip, FaTrash, FaFilePdf, FaFileAlt, FaDownload, FaSpinner,
} from 'react-icons/fa';

// ─────────────────────────────────────────────────────────────────────────────
// CONFIG
// ─────────────────────────────────────────────────────────────────────────────
const STAGES = {
  novo:          { label: 'Novos leads',      color: '#579bfc' },
  contato:       { label: 'Em contato',       color: '#a25ddc' },
  proposta:      { label: 'Proposta enviada', color: '#f5a623' },
  negociacao:    { label: 'Em negociação',    color: '#e2445c' },
  fechado_ganho: { label: 'Fechado (ganho)',  color: '#00c875' },
};

const ESTAGIOS_CONVERSAO_LIBERADA = new Set(['negociacao', 'fechado_ganho']);

const TEMPS = {
  quente: { label: 'Quente', icon: FaFire,      cls: 'lcm-temp--hot' },
  morno:  { label: 'Morno',  icon: FaSmog,      cls: 'lcm-temp--warm' },
  frio:   { label: 'Frio',   icon: FaSnowflake, cls: 'lcm-temp--cold' },
};

const CHANNELS = [
  { key: 'whatsapp', label: 'WhatsApp', icon: FaWhatsapp },
  { key: 'sms',      label: 'SMS',      icon: FaSms },
  { key: 'email',    label: 'E-mail',   icon: FaEnvelope },
  { key: 'nota',     label: 'Nota',     icon: FaRegStickyNote },
];

// Modelos rápidos por canal — poupam digitação do vendedor.
// {{nome}} e {{empresa}} são substituídos na hora de inserir.
const TEMPLATES = {
  whatsapp: [
    { rot: 'Primeiro contato', txt: 'Olá {{nome}}, aqui é da BLB System! Vi seu interesse na nossa plataforma de gestão de frota. Posso te mostrar como funciona numa call rápida?' },
    { rot: 'Follow-up',        txt: 'Oi {{nome}}, tudo bem? Passando pra saber se conseguiu avaliar a proposta que enviei pra {{empresa}}. Fico à disposição!' },
    { rot: 'Retomada',         txt: 'Olá {{nome}}! Faz um tempinho que não conversamos. A oferta pra {{empresa}} continua de pé — quer que eu te atualize?' },
  ],
  sms: [
    { rot: 'Lembrete',   txt: '{{nome}}, aqui é da BLB System. Retomando nosso contato sobre a plataforma. Pode falar agora?' },
    { rot: 'Confirmação', txt: 'Oi {{nome}}, confirmando nossa reuniao. Qualquer coisa, responda por aqui. BLB System.' },
  ],
  email: [
    { rot: 'Proposta enviada', txt: 'Olá {{nome}},\n\nSegue em anexo a proposta comercial da BLB System para a {{empresa}}.\n\nFico à disposição para esclarecer qualquer ponto.\n\nAbraço!' },
    { rot: 'Follow-up',        txt: 'Olá {{nome}},\n\nRetomando nosso contato — gostaria de saber se teve a oportunidade de avaliar nossa proposta para a {{empresa}}.\n\nPodemos marcar uma conversa esta semana?\n\nAbraço!' },
  ],
  nota: [
    { rot: 'Objeção',     txt: 'Objeção levantada: ' },
    { rot: 'Próximo passo', txt: 'Próximo passo definido: ' },
  ],
};

const STALE_DAYS = 5;

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const formatCurrency = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const getInitials = (n) =>
  (n || '').split(' ').filter(Boolean).map((x) => x[0]).slice(0, 2).join('').toUpperCase() || '?';

const formatDateTime = (v) => {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('pt-BR');
};

const daysSince = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / 86_400_000);
};

const applyVars = (txt, lead) =>
  String(txt || '')
    .replace(/\{\{nome\}\}/g, (lead?.nome_contato || '').split(' ')[0] || 'tudo bem')
    .replace(/\{\{empresa\}\}/g, lead?.nome_empresa || 'sua empresa');

// Metadados visuais de cada evento da timeline.
const eventMeta = (item) => {
  const kind = item.canal || item.tipo || 'nota';
  switch (kind) {
    case 'sms':             return { icon: FaSms,           label: 'SMS',      cls: 'sms' };
    case 'email':           return { icon: FaEnvelope,      label: 'E-mail',   cls: 'email' };
    case 'whatsapp':        return { icon: FaWhatsapp,      label: 'WhatsApp', cls: 'whatsapp' };
    case 'ligacao':         return { icon: FaPhone,         label: 'Ligação',  cls: 'call' };
    case 'mudanca_estagio': return { icon: FaHistory,       label: 'Sistema',  cls: 'system' };
    case 'sistema':         return { icon: FaHistory,       label: 'Sistema',  cls: 'system' };
    default:                return { icon: FaRegStickyNote, label: 'Nota',     cls: 'note' };
  }
};

// Heurística anti-duplicidade: se a timeline já tem um evento de conversão
// iniciada, o lead já foi convertido — não deixamos disparar de novo.
const CONVERSAO_MARKER = /convers[aã]o em tenant iniciada/i;

// ─────────────────────────────────────────────────────────────────────────────
// SUBCOMPONENTES
// ─────────────────────────────────────────────────────────────────────────────
function TempBadge({ temperatura }) {
  const t = TEMPS[temperatura] || TEMPS.morno;
  const Icon = t.icon;
  return (
    <span className={`lcm-temp ${t.cls}`} title={`Lead ${t.label}`}>
      <Icon /> {t.label}
    </span>
  );
}

function InfoRow({ icon, label, value, href }) {
  return (
    <div className="lcm-inforow">
      <span className="lcm-inforow__ic">{icon}</span>
      <div className="lcm-inforow__data">
        <span className="lcm-inforow__label">{label}</span>
        {href && value ? (
          <a className="lcm-inforow__value" href={href} target="_blank" rel="noopener noreferrer">{value}</a>
        ) : (
          <span className="lcm-inforow__value">{value || <em className="lcm-muted">—</em>}</span>
        )}
      </div>
    </div>
  );
}

const formatBytes = (n) => {
  const b = Number(n || 0);
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const isImageMime = (m) => String(m || '').startsWith('image/');

// ─────────────────────────────────────────────────────────────────────────────
// ANEXO (item da galeria)
// Puxa o binário pelo proxy autenticado como blob → funciona com cookie OU token
// (um <img src> cru não carregaria o header Authorization do axios).
// ─────────────────────────────────────────────────────────────────────────────
function AnexoItem({ anexo, onDelete, deleting }) {
  const [thumb, setThumb] = useState(null);
  const img = isImageMime(anexo.mime_type);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    if (!img) return undefined;
    (async () => {
      try {
        const { data } = await api.get(`/api/anexos/drive/${anexo.drive_file_id}`, { responseType: 'blob' });
        if (cancelled) return;
        objectUrl = URL.createObjectURL(data);
        setThumb(objectUrl);
      } catch { /* sem thumb — cai no ícone */ }
    })();
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [anexo.drive_file_id, img]);

  const abrir = async () => {
    try {
      const { data } = await api.get(`/api/anexos/drive/${anexo.drive_file_id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(data);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      toast.error('Não foi possível abrir o anexo.');
    }
  };

  const FileIcon = String(anexo.mime_type || '').includes('pdf') ? FaFilePdf : FaFileAlt;

  return (
    <div className="lcm-anexo" title={anexo.original_name || anexo.file_name}>
      <button type="button" className="lcm-anexo__open" onClick={abrir}>
        {img && thumb
          ? <img src={thumb} alt={anexo.original_name || 'anexo'} />
          : <span className="lcm-anexo__ic"><FileIcon /></span>}
      </button>
      <div className="lcm-anexo__info">
        <span className="lcm-anexo__name">{anexo.original_name || anexo.file_name}</span>
        <span className="lcm-anexo__size">{formatBytes(anexo.tamanho)}</span>
      </div>
      <div className="lcm-anexo__actions">
        <button type="button" className="lcm-anexo__btn" onClick={abrir} title="Abrir / baixar"><FaDownload /></button>
        <button type="button" className="lcm-anexo__btn lcm-anexo__btn--danger"
          onClick={() => onDelete(anexo.id)} disabled={deleting} title="Remover">
          {deleting ? <FaSpinner className="lcm-spin" /> : <FaTrash />}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MODAL PRINCIPAL
// ─────────────────────────────────────────────────────────────────────────────
export default function LeadCentralModal({ lead, isOpen, onClose, onAfterAction }) {
  const [channel, setChannel] = useState('whatsapp');
  const [timeline, setTimeline] = useState([]);
  const [loadingTl, setLoadingTl] = useState(false);
  const [body, setBody] = useState('');
  const [subject, setSubject] = useState('');
  const [sending, setSending] = useState(false);
  const [showConverter, setShowConverter] = useState(false);
  const [conversaoDisparada, setConversaoDisparada] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);

  const [anexos, setAnexos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [deletingAnexoId, setDeletingAnexoId] = useState(null);

  const feedRef = useRef(null);
  const fileInputRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!lead?.id) return;
    setLoadingTl(true);
    try {
      const { data } = await api.get(`/api/vendas/lead/${lead.id}/timeline`);
      // A rota devolve DESC; exibimos cronológico (antigo → recente), estilo chat.
      setTimeline(Array.isArray(data) ? [...data].reverse() : []);
    } catch {
      setTimeline([]);
    } finally {
      setLoadingTl(false);
    }
  }, [lead]);

  const fetchAnexos = useCallback(async () => {
    if (!lead?.id) return;
    try {
      const { data } = await api.get(`/api/vendas/lead/${lead.id}/anexos`);
      setAnexos(Array.isArray(data) ? data : []);
    } catch {
      setAnexos([]);
    }
  }, [lead]);

  useEffect(() => {
    if (isOpen) {
      setChannel('whatsapp'); setBody(''); setSubject('');
      setShowConverter(false); setConversaoDisparada(false); setShowTemplates(false);
      setAnexos([]);
      refresh();
      fetchAnexos();
    }
  }, [isOpen, refresh, fetchAnexos]);

  const handleUpload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append('arquivos', f));
      await api.post(`/api/vendas/lead/${lead.id}/anexos`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(files.length > 1 ? 'Anexos enviados.' : 'Anexo enviado.');
      await fetchAnexos();
      await refresh();
      onAfterAction?.();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Falha ao enviar o anexo.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteAnexo = async (anexoId) => {
    setDeletingAnexoId(anexoId);
    try {
      await api.delete(`/api/vendas/lead/${lead.id}/anexos/${anexoId}`);
      setAnexos((prev) => prev.filter((a) => a.id !== anexoId));
      toast.success('Anexo removido.');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Falha ao remover o anexo.');
    } finally {
      setDeletingAnexoId(null);
    }
  };

  // Fecha com ESC.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // Rola o feed pro fim sempre que a timeline muda (comportamento de chat).
  useEffect(() => {
    if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight;
  }, [timeline]);

  const jaConvertido = useMemo(
    () => conversaoDisparada
      || Number(lead?.conversao_ativa) > 0
      || timeline.some((i) => CONVERSAO_MARKER.test(i.conteudo || '')),
    [timeline, conversaoDisparada, lead]
  );

  if (!isOpen || !lead) return null;

  const stale = daysSince(lead.data_atualizacao);
  const isStale = stale !== null && stale >= STALE_DAYS && lead.estagio !== 'fechado_ganho';
  const podeConverter = ESTAGIOS_CONVERSAO_LIBERADA.has(lead.estagio) && !jaConvertido;

  const send = async () => {
    if (!body.trim()) return toast.warn('Escreva a mensagem antes de enviar.');
    setSending(true);
    try {
      if (channel === 'nota') {
        await api.post(`/api/vendas/lead/${lead.id}/nota`, { nota: body.trim() });
        toast.success('Nota registrada.');
      } else if (channel === 'sms') {
        if (!lead.telefone) return toast.warn('Este lead não tem telefone.');
        await api.post(`/api/vendas/lead/${lead.id}/sms`, { mensagem: body.trim() });
        toast.success('SMS enviado.');
      } else if (channel === 'email') {
        if (!lead.email) return toast.warn('Este lead não tem e-mail.');
        if (!subject.trim()) return toast.warn('Informe o assunto do e-mail.');
        await api.post(`/api/vendas/lead/${lead.id}/email`, { assunto: subject.trim(), corpo: body.trim() });
        toast.success('E-mail enviado.');
      } else if (channel === 'whatsapp') {
        if (!lead.telefone) return toast.warn('Este lead não tem telefone.');
        await api.post(`/api/vendas/lead/${lead.id}/whatsapp`, { mensagem: body.trim() });
        toast.success('Mensagem enviada pelo WhatsApp.');
      }
      setBody(''); setSubject(''); setShowTemplates(false);
      await refresh();
      onAfterAction?.();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Não foi possível concluir o envio.');
    } finally {
      setSending(false);
    }
  };

  const insertTemplate = (txt) => {
    setBody((prev) => (prev ? `${prev}\n${applyVars(txt, lead)}` : applyVars(txt, lead)));
    setShowTemplates(false);
  };

  const handleConverterSuccess = async () => {
    setConversaoDisparada(true);
    setShowConverter(false);
    await refresh();
    onAfterAction?.();
  };

  const maxLen = channel === 'sms' ? 160 : undefined;
  const canWrite = channel === 'nota'
    || (channel === 'email' && !!lead.email)
    || (channel !== 'email' && !!lead.telefone);

  const composerCta = channel === 'nota' ? 'Salvar nota'
    : channel === 'sms' ? 'Enviar SMS'
      : channel === 'email' ? 'Enviar e-mail' : 'Enviar WhatsApp';

  const modal = (
    <div className="lcm-overlay" onClick={onClose}>
      <div className="lcm-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">

        {/* HEADER */}
        <header className="lcm-head">
          <div className="lcm-head__identity">
            <div className="lcm-avatar" style={{ '--stage': STAGES[lead.estagio]?.color }}>
              {getInitials(lead.nome_contato)}
            </div>
            <div className="lcm-head__meta">
              <h2>{lead.nome_empresa || lead.nome_contato}</h2>
              <div className="lcm-head__pills">
                <span className="lcm-stage-pill" style={{ '--stage': STAGES[lead.estagio]?.color }}>
                  {STAGES[lead.estagio]?.label || lead.estagio}
                </span>
                <span className="lcm-head__forecast"><FaDollarSign /> {formatCurrency(lead.valor_potencial)}</span>
                <TempBadge temperatura={lead.temperatura} />
              </div>
            </div>
          </div>

          <div className="lcm-head__actions">
            {podeConverter && (
              <button type="button" className="lcm-btn lcm-btn--convert"
                onClick={() => setShowConverter(true)}
                title="Iniciar conversão deste lead em um tenant do sistema">
                <FaFileSignature /> Converter em Tenant
              </button>
            )}
            {jaConvertido && ESTAGIOS_CONVERSAO_LIBERADA.has(lead.estagio) && (
              <span className="lcm-converted-tag" title="Já existe uma conversão em andamento para este lead">
                <FaCheckDouble /> Conversão iniciada
              </span>
            )}
            <button type="button" className="lcm-icon-btn" onClick={onClose} aria-label="Fechar"><FaTimes /></button>
          </div>
        </header>

        {/* CORPO — DUAS COLUNAS */}
        <div className="lcm-body">

          {/* COLUNA ESQUERDA — DOSSIÊ */}
          <aside className="lcm-side">
            <h4 className="lcm-side__title">Dossiê do lead</h4>

            <InfoRow icon={<FaUserTie />} label="Contato" value={lead.nome_contato} />
            <InfoRow icon={<FaPhone />}   label="Telefone" value={lead.telefone}
              href={lead.telefone ? `https://wa.me/55${String(lead.telefone).replace(/\D/g, '')}` : null} />
            <InfoRow icon={<FaAt />}      label="E-mail" value={lead.email} />
            <InfoRow icon={<FaMapSigns />} label="Origem" value={lead.origem} />
            <InfoRow icon={<FaUserTie />} label="Responsável" value={lead.responsavel_nome} />

            <div className="lcm-signals">
              {Number(lead.score) > 0 && (
                <span className="lcm-signal lcm-signal--score"><FaBolt /> Score {lead.score}</span>
              )}
              {isStale && (
                <span className="lcm-signal lcm-signal--stale"><FaExclamationTriangle /> Parado há {stale}d</span>
              )}
              {lead.estagio === 'fechado_ganho' && (
                <span className="lcm-signal lcm-signal--won"><FaCheckDouble /> Ganho</span>
              )}
            </div>

            {/* ANEXOS */}
            <div className="lcm-anexos">
              <div className="lcm-anexos__head">
                <span><FaPaperclip /> Anexos {anexos.length > 0 && <em>({anexos.length})</em>}</span>
                <button type="button" className="lcm-anexos__add"
                  onClick={() => fileInputRef.current?.click()} disabled={uploading}
                  title="Adicionar proposta, contrato ou documento">
                  {uploading ? <FaSpinner className="lcm-spin" /> : <FaPaperclip />} {uploading ? 'Enviando…' : 'Anexar'}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  hidden
                  accept="image/png,image/jpeg,image/webp,application/pdf,.doc,.docx,.xls,.xlsx"
                  onChange={(e) => handleUpload(e.target.files)}
                />
              </div>

              {anexos.length === 0 ? (
                <p className="lcm-anexos__empty">Nenhum anexo. Envie propostas, contratos ou documentos do cliente.</p>
              ) : (
                <div className="lcm-anexos__list">
                  {anexos.map((a) => (
                    <AnexoItem key={a.id} anexo={a} onDelete={handleDeleteAnexo} deleting={deletingAnexoId === a.id} />
                  ))}
                </div>
              )}
            </div>

            <div className="lcm-side__hint">
              <FaInfoCircle /> Tudo o que você enviar por aqui entra no fio do histórico ao lado, com canal, autor e horário.
            </div>
          </aside>

          {/* COLUNA DIREITA — TIMELINE + COMPOSER */}
          <section className="lcm-conv">
            <div className="lcm-feed" ref={feedRef}>
              {loadingTl && <div className="lcm-feed__loading">Carregando histórico…</div>}
              {!loadingTl && timeline.length === 0 && (
                <div className="lcm-empty"><FaInfoCircle /> Nenhuma interação ainda. Comece a conversa abaixo.</div>
              )}
              {!loadingTl && timeline.map((item) => {
                const m = eventMeta(item);
                const Icon = m.icon;
                if (m.cls === 'system') {
                  return (
                    <div className="lcm-feed__system" key={item.id}>
                      <Icon /> <span>{item.conteudo}</span>
                      <time>{formatDateTime(item.data_registro)}</time>
                    </div>
                  );
                }
                const saida = item.direcao === 'saida';
                return (
                  <div className={`lcm-msg lcm-msg--${m.cls} ${saida ? 'lcm-msg--out' : 'lcm-msg--in'}`} key={item.id}>
                    <div className="lcm-msg__mark"><Icon /></div>
                    <div className="lcm-msg__bubble">
                      <div className="lcm-msg__meta">
                        <strong>{item.usuario_nome || 'BLB'}</strong>
                        <span className="lcm-msg__chan">{m.label}</span>
                        <time>{formatDateTime(item.data_registro)}</time>
                      </div>
                      <p>{item.conteudo}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* COMPOSER */}
            <div className="lcm-composer">
              <div className="lcm-chantabs">
                {CHANNELS.map((c) => {
                  const Icon = c.icon;
                  return (
                    <button key={c.key} type="button"
                      className={`lcm-chantab lcm-chantab--${c.key} ${channel === c.key ? 'is-active' : ''}`}
                      onClick={() => { setChannel(c.key); setShowTemplates(false); }}>
                      <Icon /> {c.label}
                    </button>
                  );
                })}
              </div>

              <div className="lcm-composer__target">
                {channel === 'email'
                  ? <span>Para: <strong>{lead.email || '— (sem e-mail)'}</strong></span>
                  : channel === 'nota'
                    ? <span>Anotação interna (não é enviada ao cliente)</span>
                    : <span>Para: <strong>{lead.telefone || '— (sem telefone)'}</strong></span>}
                <button type="button" className="lcm-tpl-toggle" onClick={() => setShowTemplates((s) => !s)}>
                  <FaMagic /> Modelos
                </button>
              </div>

              {showTemplates && (
                <div className="lcm-templates">
                  {(TEMPLATES[channel] || []).map((t) => (
                    <button key={t.rot} type="button" className="lcm-tpl-chip" onClick={() => insertTemplate(t.txt)}>
                      {t.rot}
                    </button>
                  ))}
                  {(TEMPLATES[channel] || []).length === 0 && <span className="lcm-muted">Sem modelos para este canal.</span>}
                </div>
              )}

              {channel === 'email' && (
                <input className="lcm-subject" placeholder="Assunto do e-mail"
                  value={subject} onChange={(e) => setSubject(e.target.value)} />
              )}

              <div className="lcm-composer__row">
                <textarea
                  className="lcm-textarea"
                  placeholder={
                    channel === 'nota' ? 'Objeções, próximos passos, detalhes da conversa…'
                      : channel === 'sms' ? 'Mensagem SMS (máx. 160 caracteres)…'
                        : channel === 'email' ? 'Corpo do e-mail…'
                          : 'Mensagem de WhatsApp…'
                  }
                  maxLength={maxLen}
                  value={body}
                  disabled={!canWrite}
                  onChange={(e) => setBody(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && canWrite && !sending) send();
                  }}
                />
                <button type="button"
                  className={`lcm-send lcm-send--${channel}`}
                  onClick={send}
                  disabled={sending || !canWrite}
                  title={canWrite ? composerCta : 'Lead sem canal de contato para este envio'}>
                  <FaPaperPlane />
                </button>
              </div>

              <div className="lcm-composer__foot">
                {channel === 'sms' && <span className="lcm-counter">{body.length}/160</span>}
                <span className="lcm-composer__tip"><FaClock /> Ctrl/⌘ + Enter envia · {composerCta}</span>
              </div>
            </div>
          </section>
        </div>
      </div>

      {showConverter && (
        <ModalConverterTenant
          lead={lead}
          onClose={() => setShowConverter(false)}
          onSuccess={handleConverterSuccess}
        />
      )}
    </div>
  );

  return createPortal(modal, document.body);
}